// src/lib/dashboard/periodoDashboard.ts
// Filtro de período do Painel Executivo geral (dashboard cross-módulo). Além
// dos presets relativos já usados no Financeiro (mês atual, últimos 12
// meses...), o dashboard também precisa deixar escolher um mês específico ou
// um período customizado arbitrário — não gated pelos meses de um dataset em
// particular, porque cada seção (Geradoras/Marketing/Comercial/Financeiro)
// tem sua própria cobertura de dados. Por isso o range aqui usa
// `<input type="month">` (formato "YYYY-MM"), não o calendário de meses
// disponíveis do SeletorPeriodoCalendario (esse continua específico do
// comparativo A/B do Financeiro).
import type { PeriodoFiltro, JanelaPeriodo } from '../financeiro/periodo.ts';
import { calcularJanelaPeriodo } from '../financeiro/periodo.ts';

export type { PeriodoFiltro, JanelaPeriodo };

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

export type PeriodoDashboard =
  | { tipo: 'preset'; preset: PeriodoFiltro }
  | { tipo: 'mesEspecifico'; mes: string }        // "YYYY-MM"
  | { tipo: 'range'; inicio: string; fim: string }; // "YYYY-MM" cada ponta

export function yyyyMmParaData(yyyyMm: string): Date | null {
  const partes = yyyyMm.split('-');
  if (partes.length !== 2) return null;
  const ano = Number(partes[0]);
  const mes = Number(partes[1]);
  if (!ano || !mes || mes < 1 || mes > 12) return null;
  return new Date(ano, mes - 1, 1);
}

export function yyyyMmParaMesReferencia(yyyyMm: string): string | null {
  const d = yyyyMmParaData(yyyyMm);
  if (!d) return null;
  return `${MESES[d.getMonth()]}/${d.getFullYear()}`;
}

export function calcularJanelaPeriodoDashboard(periodo: PeriodoDashboard, hoje: Date): JanelaPeriodo | null {
  if (periodo.tipo === 'preset') return calcularJanelaPeriodo(periodo.preset, hoje);

  if (periodo.tipo === 'mesEspecifico') {
    const d = yyyyMmParaData(periodo.mes);
    return d ? { inicio: d, fim: d } : null;
  }

  const inicio = yyyyMmParaData(periodo.inicio);
  const fim = yyyyMmParaData(periodo.fim);
  if (!inicio || !fim) return null;
  return inicio.getTime() <= fim.getTime() ? { inicio, fim } : { inicio: fim, fim: inicio };
}

// Rótulo em português de um PeriodoDashboard, para exibir em cards/legendas
// (ex.: "Mês atual", "JUL/2026", "MAR/2026 – JUL/2026"). Duplica
// deliberadamente os rótulos de preset que também existem em
// OPCOES_PERIODO_PRESET (views/Dashboard/PainelExecutivo/types.ts) — aquele é
// um array de UI com { value, label } pra popular um <select>, pensado pra
// views/; este é só texto puro, pensado pra ficar em src/lib sem depender de
// nada de views/. Duplicação pequena (10 rótulos) e deliberada, não sistêmica.
const ROTULOS_PRESET: Record<PeriodoFiltro, string> = {
  ultimos12: 'Últimos 12 meses',
  ultimos6: 'Últimos 6 meses',
  ultimos3: 'Últimos 3 meses',
  mesAtual: 'Mês atual',
  mesAnterior: 'Mês anterior',
  trimestreAtual: 'Trimestre atual',
  trimestreAnterior: 'Trimestre anterior',
  anoAtual: 'Ano atual',
  anoAnterior: 'Ano anterior',
  historico: 'Todo o histórico',
};

export function rotuloPeriodoDashboard(periodo: PeriodoDashboard): string {
  if (periodo.tipo === 'preset') return ROTULOS_PRESET[periodo.preset];
  if (periodo.tipo === 'mesEspecifico') return yyyyMmParaMesReferencia(periodo.mes) ?? 'Mês inválido';
  const inicio = yyyyMmParaMesReferencia(periodo.inicio);
  const fim = yyyyMmParaMesReferencia(periodo.fim);
  if (!inicio || !fim) return 'Período inválido';
  return inicio === fim ? inicio : `${inicio} – ${fim}`;
}
